import LockIcon from '@mui/icons-material/Lock'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  MenuItem,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useTheme,
  alpha,
} from '@mui/material'
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { useEntitiesByType } from 'api/aws/organizations_table/useEntitiesByType'
import { useItemByPKAndPrefix } from 'api/aws/organizations_table/useItemByPkAndPrefix'
import FloatingHelpButton from 'context/HelpProvider/FloatingHelpButton'
import FormHelpPopover from 'context/HelpProvider/FormHelpPopover'
import HelpNavigationBox from 'context/HelpProvider/HelpNavigation'
import HelpOverlay from 'context/HelpProvider/HelpOverlay'
import { useHelp } from 'context/HelpProvider/HelpProvider'
import { useHighlightStep } from 'context/HelpProvider/useHighlightStep'
import { useMarketingTaskFormRefs } from 'context/HelpProvider/useMarketingTaskFormRefs'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import useRawUpload from '../../api/aws/tasks/useRawUpload'
import { useSubmitTask } from '../../api/aws/tasks/useSubmitTask'
import { useCreateTask } from '../../api/aws/useCreateTask'
import { useDeals } from '../../api/aws/useDeals'
import { useUser } from '../../api/aws/useUser'
import { useUsers } from '../../api/aws/useUsers'
import { useUsersById } from '../../api/customHooks/useUsersById'
import { useCreateGoogleFolder } from '../../api/google/useCreateGoogleFolder'
import { useActiveUser } from '../../api/slack/useActiveUser'
import { useValidationContext } from '../../context/ValidationProvider/ValidationProvider'
import { BoxScroller } from '../../styles/elements/styleElements'
import { dialogStyles as formDialogStyles } from '../../styles/utilities'
import { normalizeDate } from '../../utils/dateUtils'
import { SelectedFile } from '../Comments/types'
import OnboardTeamAutocomplete from '../DataGrid/components/OnboardTeamAutocomplete'
import { NewFileAttachments } from '../TaskDrawer/NewFileAttachments'
import CreateTaskHeader from '../Tasks/CreateTaskHeader'
import TaskRichText from '../Tasks/TaskRichText'
import { calculateTaskDays, formatDateString, workday } from '../Tasks/taskUtil'
import FieldComponent from './FieldComponent'
import FormAutocomplete from './FormAutocomplete'
import FormTypeSelect from './FormTypeSelect'
import RenderTypeFields from './RenderTypeFields'
import SubtaskStep from './SubtaskStep'
import {
  adApprovalsFields,
  budgetChannelFields,
  campaignChecklistFields,
  createSetupChannelFields,
  CreativeTypeOptions,
  enableFields,
  FieldConfig,
  FieldType,
  FormConfig,
  generateDynamicSubtasks,
  handleFormatTypesString,
  initialMarketingFormData,
  initialRequestFields,
  MarketingFormConfig,
  MarketingFormData,
  MarketingFormProps,
  newClientOnboardFields,
  pauseFields,
  requestedByOptions,
  requestTypeOptions,
  reviewChannelFields,
  taskTypeLabels,
  TaskTypeOptions,
  updateChannelFields,
  User,
} from './types'

interface OptionType {
  id: string
  name?: string // For organizations
  dba?: string // DBA field if needed
  firstName?: string // For users
  lastName?: string // For users
  image_512?: string // For avatars
  org_uuid?: string
  assigner: object
  assignees: User[]
  AM: User[]
  DMS: User[]
  website: string
  deal_uuid: string
  deals: object[]
  organization: object | null
  subfolderIds?: { tasksFolderId?: string; creativeTasksFolderId?: string; [key: string]: any }
  googleDriveFolderId?: string
}
type CurrentUser = {
  user_uuid: string
  id: string
  firstName: string
  lastName: string
  name: string
  image_512: string
  role: string
  title: string
}

const channelFieldMap: Record<string, Record<string, Record<string, FieldConfig>>> = {
  reviewAudit: reviewChannelFields,
  updates: updateChannelFields,
  budget: budgetChannelFields,
  createSetup: createSetupChannelFields,
  newClientOnboard: newClientOnboardFields,
  campaignChecklists: campaignChecklistFields,
  adApprovals: adApprovalsFields,
  pause: pauseFields,
  enable: enableFields,
  // Add more mappings as needed
}

const MarketingTaskForm: React.FC<MarketingFormProps & { prefillData?: Partial<MarketingFormData> }> = ({
  open,
  onClose,
  prefillData,
}) => {
  const { PK } = useParams()
  const { currentStep, stopHelp, startHelp } = useHelp()
  const { users, isUsersLoading, isUsersError } = useUsers(true, ['users', 'creativeIntake'])
  const { data: organizations, isLoading } = useEntitiesByType(['organization'])
  const { deals, isDealsLoading } = useDeals(true, ['deals', 'creativeIntake'])
  const [selectedOrganization, setSelectedOrganization] = useState<OptionType | null>(null)
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const { data: organizationData, isLoading: isOrganizationLoading } = useItemByPKAndPrefix(
    `ORG_${selectedOrgId}`,
    'CREATION_TIMESTAMP_',
    ['organizationItem', `ORG_${selectedOrgId}`]
  )
  const organization = organizationData?.[0]
  const [selectedDeal, setSelectedDeal] = useState<OptionType | null>(null)
  const { activeUser, isActiveUserLoading } = useActiveUser()
  const { id: activeUserId } = activeUser?.user ?? {}
  const { user: currentUser, isUserLoading: isCurrentUserLoading } = useUser(activeUserId)
  const [orgDeals, setOrgDeals] = useState<object[]>([])
  const { mutate: createTaskFolder } = useCreateGoogleFolder()
  const [formData, setFormData] = useState<MarketingFormData>({
    ...initialMarketingFormData,
    ...prefillData,
    selectedSubtaskKeys: prefillData?.selectedSubtaskKeys,
    assigner: currentUser
      ? ({
          user_uuid: currentUser.user_uuid || '',
          id: currentUser.id || '',
          firstName: currentUser.firstName || '',
          lastName: currentUser.lastName || '',
          name: currentUser.name || '',
          image_512: currentUser.image_512 || '',
          role: currentUser.role || '',
          title: currentUser.title || '',
        } as User)
      : ({} as User),
    type: prefillData?.type ?? '',
  })
  const [isCreating, setIsCreating] = useState(false)
  const { mutate: createTask } = useCreateTask()
  const { submitNewTask } = useSubmitTask()
  const { validateMarketingForm, marketingFormErrors } = useValidationContext()
  const [isFormValid, setIsFormValid] = useState<boolean>(false)
  const [taskName, setTaskName] = useState(() => {
    if (prefillData?.name) return prefillData.name
    if (organization?.name && prefillData?.type)
      return `${organization.name} - ${handleFormatTypesString(prefillData.type)}`
    return organization?.name || ''
  })
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([])
  const { mutateAsync: rawUpload } = useRawUpload()
  // ✅ Use useMemo to efficiently return user IDs
  ///////////// REQUESTED INTERNAL OR CLIENT DROPDOWN NEEDED****************************
  /////////////
  const userIds = useMemo(() => {
    if (!organization) return []

    // Combine AM and DMS arrays, ensuring unique valid IDs
    const combinedIds = [...(organization.AM ?? []), ...(organization.DMS ?? [])]

    return Array.from(new Set(combinedIds.filter(Boolean))) // Remove duplicates & falsy values
  }, [organization])
  // console.log('ACTIVE USER', activeUser)

  const { users: taskAssignees, isUsersByIdLoading: isTaskAssigneesLoading } = useUsersById(userIds ?? [])
  // console.log('currentUser', currentUser, 'taskAssignees', taskAssignees)
  const [requestFields, setRequestFields] = useState<CreativeTypeOptions>(initialRequestFields)
  const theme = useTheme()
  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  const [subtaskOptions, setSubtaskOptions] = useState<Record<string, any>>(prefillData?.subtaskOptions ?? {})
  const [selectedSubtaskKeys, setSelectedSubtaskKeys] = useState<string[]>(prefillData?.selectedSubtaskKeys ?? [])
  const [subtaskDescriptions, setSubtaskDescriptions] = useState<Record<string, string>>({})

  const [step, setStep] = useState<'form' | 'subtasks'>('form')
  const {
    step0Ref,
    step1Ref,
    step2Ref,
    step3Ref,
    step4Ref,
    step5Ref,
    step6Ref,
    step7Ref,
    step8Ref,
    stepRefs,
    marketingTaskFormSteps,
  } = useMarketingTaskFormRefs()
  useHighlightStep(0, step0Ref)
  useHighlightStep(1, step1Ref)
  useHighlightStep(2, step2Ref)
  useHighlightStep(3, step3Ref)
  useHighlightStep(4, step4Ref)
  useHighlightStep(5, step5Ref)
  useHighlightStep(6, step6Ref)
  useHighlightStep(7, step7Ref)
  useHighlightStep(8, step8Ref)
  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  useEffect(() => {
    if (prefillData?.orgId && Array.isArray(organizations) && !selectedOrganization) {
      const matched = organizations.find(org => org.org_uuid === prefillData.orgId)
      if (matched) {
        setSelectedOrganization((matched as unknown) as OptionType)
        setSelectedOrgId(matched.org_uuid)
      }
    }
  }, [prefillData?.orgId, organizations, selectedOrganization])

  ///////////////////////////////////////////////////////////////////////
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }
  useEffect(() => {
    // ⛔ Don't override if prefillData.name is already present
    if (!prefillData?.name) {
      if (organization?.name && formData?.type) {
        setTaskName(`${organization.name} - ${handleFormatTypesString(formData.type)}`)
      } else if (organization?.name) {
        setTaskName(organization.name)
      } else {
        setTaskName('')
      }
    }
  }, [organization, formData?.type, prefillData?.name])

  useEffect(() => {
    if (!prefillData?.name && taskName) {
      setFormData(prev => ({
        ...prev,
        name: taskName,
      }))
    }
  }, [taskName, prefillData?.name])

  useEffect(() => {
    if (organization) {
      setFormData(prev => ({
        ...prev,
        // Don't auto-fill assignees
        assigner: currentUser
          ? ({
              user_uuid: currentUser.user_uuid || '',
              id: currentUser.id || '',
              firstName: currentUser.firstName || '',
              lastName: currentUser.lastName || '',
              name: currentUser.name || '',
              image_512: currentUser.image_512 || '',
              role: currentUser.role || '',
              title: currentUser.title || '',
            } as User)
          : prev.assigner,
      }))
    }
  }, [organization])

  const handleUserChange = (role: string, selectedUsers: User[]) => {
    const key = role.toLowerCase() // ensure it matches the formData keys
    const formattedUsers = selectedUsers.map(user => ({
      user_uuid: user?.user_uuid || user?.id,
      firstName: user.firstName,
      lastName: user.lastName,
      name: user.name,
      image_512: user.image_512,
      role: user.role,
    }))

    setFormData(prev => ({
      ...prev,
      [key]: formattedUsers,
    }))
  }
  useEffect(() => {
    console.log('currentStep', currentStep)
  }, [currentStep])

  const handleAddtlNotesChange = (newValue: string) => {
    setFormData(prev => ({ ...prev, addtlNotes: newValue }))
  }
  const handleOrganizationChange = (selectedOrg: any) => {
    if (!selectedOrg) {
      setFormData(initialMarketingFormData)
    }
    setSelectedOrganization(selectedOrg)
    setSelectedOrgId(selectedOrg?.org_uuid || null)
    const newOrgDeals = selectedOrg?.deals
    setOrgDeals(newOrgDeals || [])
    setFormData(prev => ({
      ...prev,
      orgId: selectedOrg?.org_uuid || '',
      website: selectedOrg?.website || '',
      tasksFolderId: selectedOrg?.subfolderIds?.tasksFolderId || '',
      creativeTasksFolderId: selectedOrg?.subfolderIds?.creativeTasksFolderId || '',
      googleDriveFolderId: selectedOrg?.googleDriveFolderId || '',
      assigner: currentUser
        ? ({
            user_uuid: currentUser.user_uuid || '',
            id: currentUser.id || '',
            firstName: currentUser.firstName || '',
            lastName: currentUser.lastName || '',
            name: currentUser.name || '',
            image_512: currentUser.image_512 || '',
            role: currentUser.role || '',
            title: currentUser.title || '',
          } as User)
        : prev.assigner,
    }))
  }

  const handleChange = (event: { target: { name: string; value: unknown } }) => {
    const { name, value } = event.target
    const selectedTaskType = formData.type as keyof TaskTypeOptions
    const fieldType = selectedTaskType ? FormConfig[selectedTaskType]?.[name]?.type : undefined

    const updatedFormData: MarketingFormData = {
      ...formData,
      [name]: fieldType === FieldType.MULTISELECT ? (Array.isArray(value) ? value : [value]) : value,
    }

    if (
      name === 'channel' ||
      name.toLowerCase().includes('review') ||
      name.toLowerCase().includes('update') ||
      name.toLowerCase().includes('budget') ||
      name.toLowerCase().includes('create') ||
      name.toLowerCase().includes('pause') ||
      name.toLowerCase().includes('enable') ||
      name.toLowerCase().includes('nco') ||
      name.toLowerCase().includes('checklist') ||
      name.toLowerCase().includes('newClientOnboard')
    ) {
      const newSubtasks = generateDynamicSubtasks(updatedFormData.type, updatedFormData)
      setSubtaskOptions(newSubtasks)
      setSelectedSubtaskKeys(Object.keys(newSubtasks)) // ✅ use keys only
    }

    setFormData(updatedFormData)
  }

  const fieldTypeOrder = [
    FieldType.TEXT,
    FieldType.SELECT,
    FieldType.MULTISELECT,
    FieldType.CHECKBOX,
    FieldType.NUMBER,
    FieldType.COLOR_PICKER,
    FieldType.DATE,
    FieldType.TEXTAREA, // Put textareas at the bottom
  ]
  const { fields } = useMemo(() => {
    if (!formData.type) {
      return { fields: [] }
    }

    const selectedType = formData.type as keyof TaskTypeOptions
    return {
      fields: MarketingFormConfig[selectedType] ? Object.entries(MarketingFormConfig[selectedType]) : [],
    }
  }, [formData.type])
  useEffect(() => {
    const validateForm = async () => {
      if (formData) {
        const { isValid } = await validateMarketingForm(formData) // Await the validation
        setIsFormValid(isValid)
      }
    }

    validateForm() // Call the async function
  }, [formData, formData?.type])
  const sortFieldsByType = (fields: [string, FieldConfig][]) => {
    return fields.sort((a, b) => {
      const typeA = fieldTypeOrder.indexOf(a[1].type)
      const typeB = fieldTypeOrder.indexOf(b[1].type)
      return typeA - typeB
    })
  }
  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>) => {
    const selectedType = e.target.value as keyof TaskTypeOptions
    setFormData(prev => ({
      ...prev,
      name: `${taskName}${taskTypeLabels[selectedType]}`,
      type: selectedType, // Update formData with selected type
    }))
  }
  // NEW HELPER FUNCTION
  const handleSubmitNewTask = async () => {
    setIsCreating(true)
    try {
      let uploadedUrls: string[] = []

      // STEP 1: If files are selected, upload them and get their URLs.
      if (selectedFiles.length > 0) {
        const uploadFormData = new FormData()
        selectedFiles.forEach(f => {
          const rawFile = f.file ?? (f as any).newFile
          if (rawFile instanceof File) {
            uploadFormData.append('files', rawFile)
          }
        })
        // Use mutateAsync to get a Promise that resolves to the upload result.
        const taskSK = `TASK_${uuidv4()}`
        const data = await rawUpload({
          formData: uploadFormData,
          metadata: {
            PK: `ORG_${formData?.orgId}`, // or however you're formatting it
            SK: taskSK || `TASK_${(formData as any)?.task_uuid || formData?.name?.replace(/\s/g, '_') || 'TEMP'}`, // generate something predictable if needed
          },
        })

        uploadedUrls = data.urls
      }
      const now = new Date().toISOString()
      const taskType = handleFormatTypesString(formData.type)
      const taskDays = calculateTaskDays(taskType) || 0
      let dateDue = ''
      let endDate = ''

      if (formData.startDate && taskDays > 0) {
        const dueDate = workday(formData.startDate, taskDays)
        dateDue = dueDate ? formatDateString(dueDate) : ''
        endDate = dueDate ? dueDate.toISOString() : ''
      }
      // STEP 2: Build submitData with the returned image URLs.
      const submitData = {
        ...formData,
        imageUrls: uploadedUrls, // Assign the uploaded URLs here.
        isMarketing: true,
        channel: 'Digital',
        channels: formData?.channel,
        type: formData?.type ?? 'Marketing Form',
        masterStatus: (formData as any)?.masterStatus ?? 'Not Started',
        createdTime: now,
        taskDays,
        dateDue,
        endDate,
        // selectedSubtasks: selectedSubtaskKeys,
        selectedSubtasks: selectedSubtaskKeys.map(key => ({
          key,
          description: subtaskDescriptions[key] || '',
        })),
      }

      console.log('submitData', submitData)
      //   // STEP 3: Create the task using your submitNewTask function.
      await submitNewTask({
        formData: submitData,
        setIsCreating,
        onClose,
        parentFolderId: formData?.tasksFolderId || '',
      })

      // Clear the form and file selection on success.
      setFormData({ ...initialMarketingFormData })
      setSelectedFiles([])
    } catch (error) {
      console.error('❌ Error in task creation flow:', error)
      setIsCreating(false)

      // Reset the form to avoid infinite loops
      setFormData({ ...initialMarketingFormData })
      setSelectedOrganization(null)
      setSelectedOrgId(null)
      setSelectedDeal(null)
      setOrgDeals([])
    }
  }
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      // const newFiles = Array.from(event.target.files)
      const newFiles: SelectedFile[] = Array.from(event.target.files || []).map(file => ({
        id: file.name + Date.now(),
        name: file.name,
        mimeType: file.type,
        url: '',
        previewUrl: URL.createObjectURL(file),
        file,
      }))
      // Append new files to previously selected ones
      setSelectedFiles(prevFiles => [...prevFiles, ...newFiles])
    }
  }

  const handleRemoveNewFile = (index: number) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index))
  }
  useEffect(() => {
    if (selectedFiles) {
      console.log('selectedFiles', selectedFiles)
    }
  }, [selectedFiles])

  const handleClose = (event?: any, reason?: string) => {
    if (reason && reason === 'backdropClick') return
    onClose()
    stopHelp()
  }
  const stepOrganization = useMemo(() => {
    return organizations?.find(o => o?.name === 'Grobotic Inc (test)') ?? null
  }, [organizations])
  return (
    <Box>
      <Dialog
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: formDialogStyles({ theme, disabled: !selectedOrganization, value: selectedOrganization }) }}
        open={open}
        onClose={handleClose}
        slotProps={{ backdrop: {} as any }}
        disableEscapeKeyDown
      >
        <CreateTaskHeader context={'New Task'} />
        {step === 'form' ? (
          <>
            <BoxScroller
              sx={{
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor:
                    theme.palette.mode === 'dark' ? theme.palette.background.scrollbar_thumb : alpha(theme.palette.text.primary, 0.5),
                  borderRadius: '6px',
                },
              }}
            >
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 600, margin: 'auto' }}>
                  <Grid container rowSpacing={2} columnSpacing={1} sx={{ alignItems: 'center' }}>
                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <Box ref={step0Ref}>
                        <FormAutocomplete
                          data={organizations}
                          isLoading={isLoading}
                          label={'Organizations'}
                          error={marketingFormErrors?.orgId?.value}
                          onChange={handleOrganizationChange}
                          value={selectedOrganization as any}
                          disabled={!!prefillData?.orgId} // Disable if prefillData is provided
                          shrinkLabel
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <Box ref={step1Ref}>
                        <TextField
                          select
                          label="Type"
                          name="type"
                          value={formData.type || ''} // ✅ Ensure it's always a string
                          onChange={handleTypeChange as any}
                          fullWidth
                          disabled={!selectedOrganization}
                          SelectProps={{
                            multiple: false,
                            renderValue: selected => taskTypeLabels[selected as keyof TaskTypeOptions] || '',
                          }}
                          sx={{
                            '& .MuiInputBase-root': {
                              maxHeight: '56px',
                            },
                          }}
                        >
                          {Object.entries(taskTypeLabels).map(([key, label]) => (
                            <MenuItem key={key} value={key}>
                              {label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <FormTypeSelect
                        label="Requested By"
                        value={formData.requestedBy ?? ''} // Bind to formData.type
                        options={requestedByOptions} // Pass taskTypeList
                        onChange={selectedValue => {
                          setFormData(prev => ({
                            ...prev,
                            requestedBy: selectedValue, // Update formData with selected type
                          }))
                        }}
                        disabled={!selectedOrganization}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <FormTypeSelect
                        label="Request Type"
                        value={formData.requestType ?? ''} // Bind to formData.type
                        options={requestTypeOptions} // Pass taskTypeList
                        onChange={selectedValue => {
                          setFormData(prev => ({
                            ...prev,
                            requestType: selectedValue, // Update formData with selected type
                          }))
                        }}
                        disabled={!selectedOrganization}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <TextField
                        label="Name"
                        name="name"
                        // value={formData.name}
                        value={handleFormatTypesString(formData.type)}
                        onChange={handleInputChange}
                        fullWidth
                        // error={marketingFormErrors?.name?.value}
                        InputProps={{
                          readOnly: true,
                          endAdornment: <LockIcon sx={{ color: theme.palette.text.secondary }} />,
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            maxHeight: '56px',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <TextField
                        label="Website"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        fullWidth
                        disabled={!selectedOrganization}
                        error={!selectedOrganization ? false : marketingFormErrors?.website?.value}
                        sx={{
                          '& .MuiInputBase-root': {
                            maxHeight: '56px',
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <OnboardTeamAutocomplete
                        users={users}
                        role="Assignees"
                        label="Assignee(s)"
                        onChange={handleUserChange}
                        defaultValue={formData?.assignees || []}
                        // disabled={false}
                        disabled={!selectedOrganization}
                        isLoading={isTaskAssigneesLoading}
                        // error={marketingFormErrors?.assignee?.value}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <OnboardTeamAutocomplete
                        users={users}
                        role="Assigner"
                        label="Assigner"
                        onChange={handleUserChange}
                        defaultValue={formData?.assigner ? [formData?.assigner] : []}
                        disabled={true}
                        isLoading={isActiveUserLoading || isCurrentUserLoading}

                        // error={marketingFormErrors?.assigner?.value}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12} // Set full width for textareas
                      sm={12}
                      md={12}
                      lg={12}
                      xl={12}
                    >
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DateRangePicker
                            localeText={{ start: 'Start Date', end: 'Due Date' }}
                            value={[
                              formData?.startDate ? dayjs(formData?.startDate) : null,
                              formData?.endDate ? dayjs(formData?.endDate) : null,
                            ]}
                            onChange={newValue => {
                              const [startDate, endDate] = newValue

                              setFormData(prev => ({
                                ...prev,
                                startDate: normalizeDate(startDate, 'America/New_York'),
                                endDate: normalizeDate(endDate, 'America/New_York'),
                              }))
                            }}
                            slotProps={{
                              textField: {
                                error: !selectedOrganization
                                  ? false
                                  : marketingFormErrors?.startDate?.value || marketingFormErrors.endDate?.value,
                              },
                            }}
                            sx={{
                              width: '100%',
                              '& .MuiMultiInputDateRangeField-separator': {
                                m: 0,
                              },
                              '& .MuiFormControl-root': {
                                m: 0,
                              },
                              '& .MuiInputBase-root': {
                                maxHeight: '56px',
                                bgcolor: !selectedOrganization ? theme.palette.background.disabled : '',
                                m: 0,
                              },
                            }}
                            disabled={!selectedOrganization}
                          />
                        </Box>
                      </LocalizationProvider>
                    </Grid>
                  </Grid>

                  {formData.type && (
                    <Grid
                      container
                      rowSpacing={2}
                      // columnSpacing={1}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 'bold',
                            borderBottom: '2px solid',
                            borderColor: theme.palette.primary.main,
                          }}
                        >
                          {taskTypeLabels[formData.type as keyof TaskTypeOptions]}
                        </Typography>
                      </Grid>

                      {fields.map(([key, config]) => (
                        <Grid
                          key={key}
                          item
                          xs={12}
                          sx={{
                            marginLeft: 3.5,
                            marginRight: 3.5,
                          }}
                        >
                          <FieldComponent name={key} value={formData[key] || ''} onChange={handleChange} {...config} />
                        </Grid>
                      ))}

                      {formData.type && formData.channel && channelFieldMap[formData.type] && (
                        <Grid container spacing={2}>
                          {Array.isArray(formData.channel) &&
                            formData.channel.map(channel => {
                              const channelFields = channelFieldMap[formData.type]?.[channel] || {}
                              return (
                                <Grid item xs={12} key={channel}>
                                  <RenderTypeFields
                                    formData={formData}
                                    channel={channel}
                                    channelFields={channelFields}
                                    onChange={handleChange}
                                  />
                                </Grid>
                              )
                            })}
                        </Grid>
                      )}
                    </Grid>
                  )}

                  <Grid
                    item
                    xs={12} // Set full width for textareas
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    pt={1}
                  >
                    <TaskRichText content={formData.addtlNotes} onContentChange={handleAddtlNotesChange} />
                  </Grid>
                </Box>
              </DialogContent>
            </BoxScroller>
            <DialogActions>
              <Grid container>
                {/* Render previews for newly selected files */}

                <Grid item xs={4.5}>
                  {selectedFiles.length > 0 && (
                    <NewFileAttachments selectedFiles={selectedFiles} onRemoveFile={handleRemoveNewFile} size={28} />
                  )}
                </Grid>
                <Grid item xs={2.5}>
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{
                      marginRight: 1,
                    }}
                  >
                    Attach Files
                    <input
                      type="file"
                      accept="image/*,application/pdf,.doc,.docx"
                      multiple
                      hidden
                      onChange={e => {
                        handleImageUpload(e)
                        e.target.value = '' // Reset the input value so the same file can be added again.
                      }}
                    />
                  </Button>
                </Grid>
                <Grid item xs={2.5}>
                  <Button
                    sx={{
                      marginRight: 1,
                      backgroundColor: 'rgb(244, 67, 54, 0.2)',
                      '&:hover': {
                        backgroundColor: 'rgb(244, 67, 54, 0.7)',
                      },
                    }}
                    onClick={handleClose}
                    color="primary"
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                </Grid>
                {/* <Grid item xs={2.5}>
                  <Button
                    color='secondary'
                    variant='contained'
                    // onClick={() => console.log('[Create Button] formData', formData)}
                    onClick={handleSubmitNewTask}
                    // onClick={() => console.log('formData', formData)}
                    disabled={!isFormValid || isCreating}
                  >
                    Create Task
                  </Button>
                </Grid> */}
                <Grid item xs={2}>
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={() => {
                      if (isFormValid) setStep('subtasks')
                    }}
                    disabled={!isFormValid || isCreating}
                  >
                    Next
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </>
        ) : (
          <SubtaskStep
            subtaskOptions={subtaskOptions}
            selectedSubtaskKeys={selectedSubtaskKeys}
            setSelectedSubtaskKeys={setSelectedSubtaskKeys}
            subtaskDescriptions={subtaskDescriptions}
            setSubtaskDescriptions={setSubtaskDescriptions}
            onBack={() => setStep('form')}
            onSubmit={handleSubmitNewTask}
            isCreating={isCreating}
          />
        )}
        <Stack
          sx={{
            position: 'fixed',
            top: 75,
            left: '85%',
            transform: 'translateX(-70%)',
            backgroundColor: theme.palette.background.input,
            color: 'white',
            padding: '8px 16px',
            borderRadius: 2,
            boxShadow: 6,
            zIndex: 1402, // VERY important
            animation: 'flowText 8s linear infinite',
          }}
        >
          <Typography width={'100%'}>
            This form will create a new task attached to the selected organization and will be populated in Team Tasks.
          </Typography>
          <Typography sx={{ color: theme.palette.text.warning, width: '100%' }}>
            -- To create tasks attached to a specific onboard or project:
          </Typography>
          <Typography>visit Projects or NCO and click the row name to display the drawer</Typography>
        </Stack>
      </Dialog>
      <HelpOverlay />
      <FormHelpPopover
        steps={marketingTaskFormSteps}
        anchorEl={currentStep !== null ? stepRefs[currentStep]?.current : null}
        stepIndex={currentStep !== null ? currentStep : 0}
      />
      <HelpNavigationBox totalSteps={marketingTaskFormSteps.length} />
      <FloatingHelpButton startHelp={() => startHelp('marketingTaskForm')} />
    </Box>
  )
}

export default MarketingTaskForm
